import {
  Box, MenuItem, Select, Stack, TextField, ToggleButton,
  ToggleButtonGroup, Typography,
} from '@mui/material'

const DAYS_FULL = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday']
const DAY_KEYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun']
const DAY_LETTERS = ['M', 'T', 'W', 'T', 'F', 'S', 'S']
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
const HOURS = Array.from({ length: 24 }, (_, i) => ({ value: String(i), label: `${i % 12 || 12}:00 ${i < 12 ? 'AM' : 'PM'}` }))

const FREQUENCIES = [
  { value: 'every_hour', label: 'Every hour' },
  { value: 'every_day', label: 'Every day' },
  { value: 'every_week', label: 'Every week' },
  { value: 'every_month', label: 'Every month' },
  { value: 'every_quarter', label: 'Every 3 months' },
  { value: 'every_year', label: 'Every year' },
  { value: 'custom', label: 'Custom schedule...' },
]

function ordinal(n: number) { return n === 1 ? '1st' : n === 2 ? '2nd' : n === 3 ? '3rd' : `${n}th` }

interface Props { config: Record<string, any>; onChange: (c: Record<string, any>) => void }

export function ScheduleConfig({ config, onChange }: Props) {
  const freq = config.frequency ?? ''
  const set = (k: string, v: any) => onChange({ ...config, [k]: v })

  return (
    <Stack spacing={2} sx={{ mt: 1 }}>
      {/* Frequency */}
      <Box>
        <Label>How often?</Label>
        <Select value={freq} onChange={(e) => onChange({ ...config, frequency: e.target.value })} displayEmpty size="small" fullWidth sx={selSx}>
          <MenuItem value="" sx={miSx}>Select frequency</MenuItem>
          {FREQUENCIES.map((f) => <MenuItem key={f.value} value={f.value} sx={miSx}>{f.label}</MenuItem>)}
        </Select>
      </Box>

      {/* ── Preset configs ── */}
      {freq === 'every_day' && (
        <Box><Label>At what time?</Label><TimePicker config={config} set={set} /></Box>
      )}

      {freq === 'every_week' && (<>
        <Box>
          <Label>On which day?</Label>
          <Select value={config.day_of_week ?? 'mon'} onChange={(e) => set('day_of_week', e.target.value)} size="small" fullWidth sx={selSx}>
            {DAY_KEYS.map((k, i) => <MenuItem key={k} value={k} sx={miSx}>{DAYS_FULL[i]}</MenuItem>)}
          </Select>
        </Box>
        <Box><Label>At what time?</Label><TimePicker config={config} set={set} /></Box>
      </>)}

      {(freq === 'every_month' || freq === 'every_quarter') && (<>
        <Box>
          <Label>On which day of the month?</Label>
          <Select value={config.day_of_month ?? '1'} onChange={(e) => set('day_of_month', e.target.value)} size="small" fullWidth sx={selSx}>
            {Array.from({ length: 31 }, (_, i) => i + 1).map((d) => <MenuItem key={d} value={String(d)} sx={miSx}>{ordinal(d)}</MenuItem>)}
          </Select>
        </Box>
        <Box><Label>At what time?</Label><TimePicker config={config} set={set} /></Box>
      </>)}

      {freq === 'every_year' && (<>
        <Box>
          <Label>In which month?</Label>
          <Select value={config.month ?? '1'} onChange={(e) => set('month', e.target.value)} size="small" fullWidth sx={selSx}>
            {MONTHS.map((m, i) => <MenuItem key={i} value={String(i + 1)} sx={miSx}>{m}</MenuItem>)}
          </Select>
        </Box>
        <Box>
          <Label>On which day?</Label>
          <Select value={config.day_of_month ?? '1'} onChange={(e) => set('day_of_month', e.target.value)} size="small" fullWidth sx={selSx}>
            {Array.from({ length: 31 }, (_, i) => i + 1).map((d) => <MenuItem key={d} value={String(d)} sx={miSx}>{ordinal(d)}</MenuItem>)}
          </Select>
        </Box>
        <Box><Label>At what time?</Label><TimePicker config={config} set={set} /></Box>
      </>)}

      {/* ── Custom config ── */}
      {freq === 'custom' && (<>
        <Box>
          <Label>Repeat every</Label>
          <Stack direction="row" spacing={1.5}>
            <TextField value={config.interval ?? '1'} onChange={(e) => set('interval', e.target.value.replace(/\D/g, ''))}
              size="small" type="number" sx={{ width: 70, '& .MuiOutlinedInput-root': { fontSize: 12.5 } }} inputProps={{ min: 1 }} />
            <Select value={config.interval_unit ?? 'days'} onChange={(e) => set('interval_unit', e.target.value)} size="small" sx={{ flex: 1, ...selSx }}>
              <MenuItem value="hours" sx={miSx}>Hour(s)</MenuItem>
              <MenuItem value="days" sx={miSx}>Day(s)</MenuItem>
              <MenuItem value="weeks" sx={miSx}>Week(s)</MenuItem>
              <MenuItem value="months" sx={miSx}>Month(s)</MenuItem>
            </Select>
          </Stack>
        </Box>

        {config.interval_unit === 'weeks' && (
          <Box>
            <Label>On these days</Label>
            <ToggleButtonGroup value={config.days ?? ['mon']} onChange={(_, v) => set('days', v)} size="small" sx={{ gap: 0.5, flexWrap: 'wrap' }}>
              {DAY_KEYS.map((k, i) => (
                <ToggleButton key={k} value={k}
                  sx={{ width: 32, height: 32, borderRadius: '50% !important', border: '1px solid', borderColor: 'divider !important',
                    fontSize: 11, fontWeight: 700,
                    '&.Mui-selected': { bgcolor: '#0D9488', color: '#fff', '&:hover': { bgcolor: '#0F766E' } } }}>
                  {DAY_LETTERS[i]}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
          </Box>
        )}

        {config.interval_unit === 'months' && (
          <Box>
            <Label>On day of the month</Label>
            <Select value={config.day_of_month ?? '1'} onChange={(e) => set('day_of_month', e.target.value)} size="small" fullWidth sx={selSx}>
              {Array.from({ length: 31 }, (_, i) => i + 1).map((d) => <MenuItem key={d} value={String(d)} sx={miSx}>{ordinal(d)}</MenuItem>)}
            </Select>
          </Box>
        )}

        {config.interval_unit !== 'hours' && (
          <Box><Label>At what time?</Label><TimePicker config={config} set={set} showMinutes /></Box>
        )}
      </>)}

      {/* Summary */}
      {freq && (
        <Box sx={{ p: 1.5, borderRadius: 1.5,
          bgcolor: (t) => t.palette.mode === 'light' ? '#F0FDF4' : 'rgba(5,150,105,0.06)',
          border: '1px solid', borderColor: (t) => t.palette.mode === 'light' ? '#BBF7D0' : 'rgba(5,150,105,0.2)' }}>
          <Typography sx={{ fontSize: 12, color: (t) => t.palette.mode === 'light' ? '#166534' : '#86EFAC', lineHeight: 1.5 }}>
            📅 {summarize(config)}
          </Typography>
        </Box>
      )}
    </Stack>
  )
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function Label({ children }: { children: string }) {
  return <Typography sx={{ fontSize: 11.5, fontWeight: 600, color: 'text.secondary', mb: 0.5 }}>{children}</Typography>
}

function TimePicker({ config, set, showMinutes }: { config: Record<string, any>; set: (k: string, v: any) => void; showMinutes?: boolean }) {
  return (
    <Stack direction="row" spacing={1.5}>
      <Select value={config.hour ?? '9'} onChange={(e) => set('hour', e.target.value)} size="small" sx={{ flex: 1, ...selSx }}>
        {HOURS.map((h) => <MenuItem key={h.value} value={h.value} sx={miSx}>{h.label}</MenuItem>)}
      </Select>
      {showMinutes && (
        <Select value={config.minute ?? '0'} onChange={(e) => set('minute', e.target.value)} size="small" sx={{ width: 80, ...selSx }}>
          {[0, 15, 30, 45].map((m) => <MenuItem key={m} value={String(m)} sx={miSx}>:{String(m).padStart(2, '0')}</MenuItem>)}
        </Select>
      )}
    </Stack>
  )
}

const selSx = { fontSize: 12.5 } as const
const miSx = { fontSize: 12.5 } as const

function fmtTime(c: Record<string, any>) {
  const h = parseInt(c.hour ?? '9')
  const m = parseInt(c.minute ?? '0')
  return `${h % 12 || 12}:${String(m).padStart(2, '0')} ${h < 12 ? 'AM' : 'PM'}`
}

function summarize(c: Record<string, any>): string {
  const f = c.frequency ?? ''
  const t = fmtTime(c)

  switch (f) {
    case 'every_hour': return 'Runs every hour'
    case 'every_day': return `Runs every day at ${t}`
    case 'every_week': {
      const day = DAYS_FULL[DAY_KEYS.indexOf(c.day_of_week ?? 'mon')] ?? 'Monday'
      return `Runs every ${day} at ${t}`
    }
    case 'every_month': return `Runs on the ${ordinal(parseInt(c.day_of_month ?? '1'))} of every month at ${t}`
    case 'every_quarter': return `Runs on the ${ordinal(parseInt(c.day_of_month ?? '1'))} every 3 months at ${t}`
    case 'every_year': {
      const mo = MONTHS[parseInt(c.month ?? '1') - 1] ?? 'Jan'
      return `Runs on ${mo} ${ordinal(parseInt(c.day_of_month ?? '1'))} every year at ${t}`
    }
    case 'custom': {
      const n = parseInt(c.interval) || 1
      const u = c.interval_unit ?? 'days'
      let s = `Runs every ${n > 1 ? n + ' ' : ''}${u.replace(/s$/, '')}${n > 1 ? 's' : ''}`
      if (u === 'weeks' && c.days?.length) s += ` on ${c.days.join(', ')}`
      if (u === 'months' && c.day_of_month) s += ` on the ${ordinal(parseInt(c.day_of_month))}`
      if (u !== 'hours') s += ` at ${t}`
      return s
    }
    default: return 'Select a frequency'
  }
}
